import { useEffect } from "react";
import useRefreshToken, { setAuth } from "./useRefreshToken";
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { Navigate } from "react-router-dom";
import PageRoutes, { APIRoutes } from "../utils/constants";
import ConfigData from "../Config/config.json";
import { clearGaUserId } from "../utils/gaUser";
import { notifyAuthChanged } from "../v2/hooks/useAuthState";

const data: any = ConfigData;
const website: any = process.env.REACT_APP_ENV;
const api_url = data[website]?.api_url;

export type authDetailsType = {
  AccessToken: string;
  RefreshToken: string;
  UserID: string | number;
  LiniusAccessToken?: string;
  LiniusRefreshToken?: string;

  FirstName: string;
  LastName: string;

  Email: string;
};

export const getCurrentAuth = () => {
  const localAuth = localStorage.getItem("auth");
  const auth: authDetailsType = localAuth
    ? JSON.parse(localStorage.getItem("auth") || "")
    : {};
  return auth;
};

export const Logout = () => {
  const auth = getCurrentAuth();
  localStorage.removeItem("auth");
  // HTPTV-682: drop the GA4 user_id so a shared browser doesn't carry it over.
  clearGaUserId();
  // Mirror the header logout path so AuthChannelSync's logout branch clears
  // the stale commonDetails slice here too, not just on manual logout.
  notifyAuthChanged();
  // Best-effort server-side revocation (deletes the RT from Redis). Errors
  // (e.g. expired AccessToken) are swallowed - local cleanup already happened.
  if (auth?.AccessToken) {
    axios
      .post(
        `${api_url}${APIRoutes.Logout}`,
        {},
        {
          headers: { Authorization: `bearer ${auth.AccessToken}` },
          timeout: 3000,
        }
      )
      .catch(() => {});
  }
};

const useAxiosPrivate = (axiosInstance: AxiosInstance) => {
  const refresh = useRefreshToken();
  // var auth = useTypedSelector(getAuthState, shallowEqual);
  // const dispatch = useDispatch();
  const auth = getCurrentAuth();
  useEffect(() => {
    const requestIntercept = axiosInstance.interceptors.request.use(
      // @ts-ignore: Unreachable code error
      (config: AxiosRequestConfig) => {
        if (!config.headers!["Authorization"]) {
          config.headers!["Authorization"] = `bearer ${auth?.AccessToken}`;
        }
        return config;
      },
      (error) => {
        Promise.reject(error);
      }
    );

    const responseIntercept = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        const statusCode = error.response ? error.response.status : null;
        if (statusCode === 401 && !prevRequest._retry) {
          prevRequest._retry = true;

          const newaccess: any = await refresh();
          if (!newaccess) {
            // dispatch(logout());
            Logout();
          } else {
            axiosInstance.defaults.headers.common[
              "Authorization"
            ] = `bearer ${newaccess}`;
            prevRequest.headers["Authorization"] = `bearer ${newaccess}`;
            return axiosInstance(prevRequest);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(requestIntercept);
      axiosInstance.interceptors.response.eject(responseIntercept);
    };
  }, [auth, refresh]);

  return axiosInstance;
};

export default useAxiosPrivate;
